'use client';
import { Icon } from '@fm-nx/svg';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Fragment, useCallback, useEffect, useState } from 'react';
import LinkWrapper from '../link-wrapper/link-wrapper';
import Logo from '../logo/logo';
import MainNavLinkMenu from './main-nav-link-menu';
import { MainNavLinkNoMenu } from './main-nav-link-no-menu';
import classNames from 'classnames';
const SearchOverlay = dynamic(() => import('../search-overlay/search-overlay'));
const MainNavAbout = dynamic(() => import('../main-nav-about/main-nav-about'));
const MainNavPartnersPlatforms = dynamic(() => import('../main-nav-partners-platforms/main-nav-partners-platforms'));
const MainNavServices = dynamic(() => import('../main-nav-services/main-nav-services'));

type MainNavPropTypes = {
    variant?: 'light' | 'dark' | 'dark-transparent';
};

const navItems = [
    { title: 'Expertise', href: '/expertise', component: <MainNavServices /> },
    {
        title: ['Platforms &', 'Partnerships'],
        href: '/platforms-and-partners',
        component: <MainNavPartnersPlatforms />,
    },
    { title: 'Case Studies', href: '/case-studies' },
    { title: 'About', href: '/about', component: <MainNavAbout /> },
    { title: 'Contact', href: '/contact' },
];

const PopoverContent = ({
    open,
    close,
    setIsMenuOpen,
    children,
}: {
    open: boolean;
    close: () => void;
    setIsMenuOpen: (value: boolean) => void;
    children: (props: { open: boolean; close: () => void }) => JSX.Element;
}) => {
    // Sync popover's open state with parent
    useEffect(() => {
        setIsMenuOpen(open);
    }, [open, setIsMenuOpen]);

    return children({ open, close });
};

// Wrapper to sync Popover's internal state with parent component
// Headless UI doesn't support controlled Popover state
// Reference: https://github.com/tailwindlabs/headlessui/discussions/975
const PopoverWithSync = ({
    children,
    setIsMenuOpen,
    className,
    as,
}: {
    children: (props: { open: boolean; close: () => void }) => JSX.Element;
    setIsMenuOpen: (value: boolean) => void;
    className?: string;
    as?: React.ElementType;
}) => {
    return (
        <Popover className={className} as={as}>
            {({ open, close }) => (
                <PopoverContent open={open} close={close} setIsMenuOpen={setIsMenuOpen}>
                    {children}
                </PopoverContent>
            )}
        </Popover>
    );
};

export const MainNav = (props: MainNavPropTypes) => {
    const { variant = 'light' } = props;
    const isDarkVariant = variant === 'dark' || variant === 'dark-transparent';
    const hasBackground = variant === 'dark';
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isMenuOpenDelayed, setIsMenuOpenDelayed] = useState(false);
    const pathname = usePathname();
    const [, updateState] = useState(0);
    const forceUpdate = useCallback(() => updateState((c) => c + 1), []);

    const showSearch = false;

    // Sync delayed state with menu open state, matching transition timing
    useEffect(() => {
        if (isMenuOpen) {
            // When opening, delay the theme change to match fade-in
            const timer = setTimeout(() => setIsMenuOpenDelayed(true), 100);
            return () => clearTimeout(timer);
        } else {
            // When closing, delay the theme change until fade-out completes
            const timer = setTimeout(() => setIsMenuOpenDelayed(false), 200);
            return () => clearTimeout(timer);
        }
    }, [isMenuOpen]);

    // Manage body overflow when search is open
    useEffect(() => {
        if (isSearchOpen) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
    }, [isSearchOpen]);

    const isOpen = isSearchOpen || isMenuOpen;
    const isDark = isSearchOpen || isMenuOpenDelayed || isDarkVariant;
    // console.log({ isOpen, isDark, isMenuOpen, isSearchOpen, isMenuOpenDelayed });

    return (
        <div className={classNames('absolute top-0 z-50 w-full transition-all', { 'bg-gray-900': hasBackground })}>
            <div className="w-full mx-auto max-w-container">
                <nav className="">
                    <div className={`z-50 w-full`}>
                        <div className={`py-8 mx-6 md:mx-0 ${isSearchOpen ? 'bg-gray-900' : ''} `}>
                            <div className="grid grid-cols-4 md:grid-cols-14">
                                <div className="col-span-2 md:col-span-3 md:col-start-2">
                                    <div className="md:w-36 lg:w-64">
                                        <Link href="/" className="block group">
                                            <Logo mode={isDark ? 'light' : 'dark'} />
                                        </Link>
                                    </div>
                                </div>

                                <div className="items-center justify-end hidden col-span-9 md:flex">
                                    <Transition
                                        show={!showSearch}
                                        enter="transition-opacity duration-200"
                                        enterFrom="opacity-0"
                                        enterTo="opacity-100"
                                        leave="transition-opacity duration-100 delay-150"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                    >
                                        <ul
                                            className={`flex items-center justify-end gap-6 basis-[min-content] xl:basis-auto ${
                                                showSearch ? 'lg:hidden' : 'lg:flex'
                                            }`}
                                        >
                                            {navItems.map((link, index) => {
                                                const { title, href, component } = link;
                                                const isActiveSection = pathname?.includes(
                                                    href.toLowerCase() as string
                                                );

                                                if (!component) {
                                                    return (
                                                        <li key={`${href}-desktop`}>
                                                            <MainNavLinkNoMenu
                                                                href={href}
                                                                title={title}
                                                                isActiveSection={!!isActiveSection}
                                                                isOpen={isOpen}
                                                                onClick={() => setIsMenuOpen(false)}
                                                                isDark={isDark}
                                                            />
                                                        </li>
                                                    );
                                                } else {
                                                    return (
                                                        <PopoverWithSync
                                                            key={`${link.title}}-desktop`}
                                                            className={`flex items-center`}
                                                            as="li"
                                                            setIsMenuOpen={setIsMenuOpen}
                                                        >
                                                            {({ open, close }) => {
                                                                return (
                                                                    <>
                                                                        <MainNavLinkMenu
                                                                            key={href}
                                                                            title={title}
                                                                            isActiveSection={!!isActiveSection}
                                                                            isMenuOpenThis={open}
                                                                            onClick={() => {
                                                                                setIsMenuOpen(!open);
                                                                                setIsSearchOpen(false);
                                                                            }}
                                                                            onClickOutside={() => {
                                                                                if (isSearchOpen) return;
                                                                                setIsMenuOpen(false);
                                                                                close();
                                                                                forceUpdate();
                                                                            }}
                                                                            isDark={isDark}
                                                                        />

                                                                        <Transition
                                                                            as={Fragment}
                                                                            show={open}
                                                                            appear={true}
                                                                            enter="transition-opacity duration-200"
                                                                            enterFrom="opacity-0"
                                                                            enterTo="opacity-100"
                                                                            leave="transition-opacity duration-200 delay-150"
                                                                            leaveFrom="opacity-100"
                                                                            leaveTo="opacity-0"
                                                                        >
                                                                            <PopoverPanel
                                                                                className="w-full mx-auto max-w-container left-[50%] absolute top-0 -z-10 transition-all duration-300"
                                                                                style={{
                                                                                    transform: 'translateX(-50%)',
                                                                                }}
                                                                            >
                                                                                <div className="pt-40 pb-20 bg-gray-900">
                                                                                    {component}
                                                                                </div>
                                                                            </PopoverPanel>
                                                                        </Transition>
                                                                    </>
                                                                );
                                                            }}
                                                        </PopoverWithSync>
                                                    );
                                                }
                                            })}

                                            {/* Search Button and Overlay */}
                                            <Popover className={`flex items-center`} as="li" key={`search-nav`}>
                                                {({ open }) => {
                                                    return (
                                                        <>
                                                            <PopoverButton
                                                                className="flex items-center cursor-pointer group"
                                                                onClick={(props) => {
                                                                    setIsMenuOpen(false);
                                                                    setIsSearchOpen(!open);
                                                                }}
                                                            >
                                                                {!open && (
                                                                    <>
                                                                        <span className="sr-only">Open Search</span>{' '}
                                                                        <Icon
                                                                            name="magnifying-glass"
                                                                            color={isDark ? 'white' : 'black'}
                                                                        />
                                                                    </>
                                                                )}
                                                                {open && (
                                                                    <>
                                                                        <span className="sr-only">Close Search</span>{' '}
                                                                        <Icon name="close" color="chartreuse" />
                                                                    </>
                                                                )}
                                                            </PopoverButton>
                                                            <Transition
                                                                as={Fragment}
                                                                show={open}
                                                                enter="transition-opacity duration-200"
                                                                enterFrom="opacity-0"
                                                                enterTo="opacity-100"
                                                                leave="transition-opacity duration-100 delay-150"
                                                                leaveFrom="opacity-100"
                                                                leaveTo="opacity-0"
                                                            >
                                                                <PopoverPanel className="absolute top-0 left-0 w-full -z-10">
                                                                    <div className="h-screen pt-40 overflow-y-scroll bg-gray-900">
                                                                        <div className="w-full mx-auto max-w-container">
                                                                            <div className="grid grid-cols-14">
                                                                                <div className="col-span-12 col-start-2">
                                                                                    <SearchOverlay />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </PopoverPanel>
                                                            </Transition>
                                                        </>
                                                    );
                                                }}
                                            </Popover>
                                        </ul>
                                    </Transition>
                                </div>
                            </div>

                            <LinkWrapper href="/" className="-ml-3 lg:-ml-0 lg:shrink-0">
                                <span className="sr-only">Home</span>
                            </LinkWrapper>
                        </div>
                    </div>
                </nav>
            </div>
        </div>
    );
};

export default MainNav;
